/* Requests made of a node, and why each one is or is not moving.
 *
 * "I asked for it and nothing is happening" is the question this feature
 * generates most, and it has four different answers that look identical from
 * the outside: the window is shut, the node has not checked in, the audio was
 * evicted before it could ship, or it is simply still going. Each needs a
 * different response from the operator, so each is said rather than left to be
 * inferred from a timestamp and a byte count.
 */
import { changeArchiveJob, fmtBytes, fmtTime, type ArchiveJob } from '../api'

function hhmm(min: number | null): string {
  if (min === null) return ''
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:`
    + `${String(min % 60).padStart(2, '0')}`
}

/** What this job asked for, in words rather than in three columns. */
function scope(j: ArchiveJob): string {
  if (j.format_kind === 'video') return `Camera video: ${scopeOf(j)}`
  return scopeOf(j)
}

function scopeOf(j: ArchiveJob): string {
  const win = j.window_start_min !== null
    ? `, only ${hhmm(j.window_start_min)}–${hhmm(j.window_end_min)}`
      + `${j.window_tz ? ` ${j.window_tz.split('/').pop()}` : ''}`
    : ''
  if (j.mode === 'rolling') return `the last ${j.rolling_hours} h, rolling${win}`
  if (j.mode === 'continuous') return `everything from now on${win}`
  return `${fmtTime(j.from_utc ?? '')} → ${fmtTime(j.to_utc ?? '')}${win}`
}

/** The one sentence an operator actually wants. */
function why(j: ArchiveJob): string {
  if (j.state === 'done') return 'Finished.'
  if (j.state === 'cancelled') return 'Cancelled.'
  if (j.state === 'expired') return 'Expired before it finished.'
  if (j.state === 'paused') return 'Paused.'
  if (!j.window_open_now) {
    return j.next_window_utc
      ? `Waiting for its window — next opens ${fmtTime(j.next_window_utc)}.`
      : 'Waiting for its window.'
  }
  if (!j.last_progress_at) {
    // Distinguishing this from "nothing to send" is the whole reason the node
    // now reports which revision it is acting on.
    return 'Window is open. Nothing has arrived yet — the node picks up new '
      + 'requests on its next check-in.'
  }
  return `Window is open. Last arrival ${fmtTime(j.last_progress_at)}.`
}

export function ArchiveJobs({
  missionId, jobs, canWrite, onChanged, note,
}: {
  missionId: string
  jobs: ArchiveJob[]
  canWrite: boolean
  onChanged: () => void
  /** In place of the note about audio eviction, for a list that is not audio. */
  note?: string
}) {
  if (!jobs.length) return null

  const act = async (j: ArchiveJob, verb: 'cancel' | 'pause' | 'resume') => {
    try {
      await changeArchiveJob(missionId, j.job_id, verb)
    } finally {
      onChanged()
    }
  }

  return (
    <div className="card flush">
      <div className="card-head">
        <h2>Requests to the node</h2>
        <span className="faint" style={{ fontSize: 13 }}>
          converged on, not delivered
        </span>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>Asked for</th><th>State</th>
            <th className="r">Arrived</th><th>Why</th><th />
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.job_id}>
              <td data-l="Asked for">
                {j.label && <div>{j.label}</div>}
                <span className="faint num" style={{ fontSize: 12 }}>{scope(j)}</span>
              </td>
              <td data-l="State">
                <span className={`badge${j.state === 'active' && j.window_open_now ? ' on' : ''}`}>
                  {j.state}
                </span>
              </td>
              <td data-l="Arrived" className="r num">
                {j.segments_stored > 0
                  ? <>{fmtBytes(j.bytes_stored)}<br />
                      <span className="faint" style={{ fontSize: 12 }}>
                        {j.segments_stored} segment{j.segments_stored === 1 ? '' : 's'}
                      </span></>
                  : '—'}
                {/* Against the cap, when there is one: "2 GB" means nothing
                    without knowing whether that is all of it. */}
                {j.max_bytes && (
                  <div className="faint" style={{ fontSize: 12 }}>
                    of {fmtBytes(j.max_bytes)}
                  </div>
                )}
              </td>
              <td data-l="Why" className="faint">{why(j)}</td>
              <td>
                {canWrite && j.state === 'active' && (
                  <button className="btn ghost sm"
                    onClick={() => void act(j, 'cancel')}>Stop</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="faint" style={{ fontSize: 12, margin: '10px 14px 14px', maxWidth: '74ch' }}>
        {note ?? <>A request cannot make the node keep audio longer than it already does.
        It moves the requested hours to the back of the queue for deletion, so
        they survive until they can be sent — and if the card fills first, they
        go anyway. That trade costs newer audio: the node discards recent
        unrequested recordings before it gives up something you asked for.</>}
      </p>
    </div>
  )
}
